import { useEffect, useState } from 'react';
import { Building, Building2, Check, Filter, Loader2 } from 'lucide-react';
import { Company } from 'src/pages/billing/types/billing.types';

interface CompanyMultiSelectorProps {
  isLoading: boolean;
  companies: Company[];
  selectedCompanies: Company[];
  onCompanySelect: (company: Company) => void;
}

const CompanyMultiSelector = ({
  isLoading,
  companies,
  selectedCompanies,
  onCompanySelect,
}: CompanyMultiSelectorProps) => {
  const [companyFilter, setCompanyFilter] = useState('');
  const [showOnlySelectedCompanies, setShowOnlySelectedCompanies] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);

  const handleCompanyFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyFilter(e.target.value);
  };

  const handleCompanySelect = (company: Company) => {
    onCompanySelect(company);
  };

  useEffect(() => {
    if (companyFilter.length === 0) {
      setFilteredCompanies(showOnlySelectedCompanies ? selectedCompanies : companies);
    } else {
      showOnlySelectedCompanies ? setFilteredCompanies(selectedCompanies.filter((company) =>
        company.company_name?.toLowerCase().includes(companyFilter?.toLowerCase())
      )) : setFilteredCompanies(companies.filter((company) =>
        company.company_name?.toLowerCase().includes(companyFilter?.toLowerCase())
      ));
    }
  }, [companyFilter, companies, showOnlySelectedCompanies]);

  return (
    <div className='w-1/4 bg-white rounded-md overflow-hidden shadow-sm mb-1'>
      <div className='flex flex-col'>
        <div className='flex flex-row items-center bg-gray-201 font-medium text-black p-2 gap-2 pl-4 borber-b border-blue-500 border-b-2 pb-1 justify-between'>
          <div className='flex flex-row items-center gap-2'>
            <Building className='size-4' />
            <span className='text-black'>
              Companies
            </span>
          </div>
          <div className='flex flex-row items-center gap-2'>
            {selectedCompanies.length > 0 && showOnlySelectedCompanies ? (
              <Filter
                className='size-5 cursor-pointer text-white bg-blue-500 rounded-sm p-0.5'
                onClick={() => setShowOnlySelectedCompanies(!showOnlySelectedCompanies)}
              />
            ) : (
              <Filter
                className='size-4 cursor-pointer'
                onClick={() => setShowOnlySelectedCompanies(!showOnlySelectedCompanies)}
              />
            )}
          </div>
        </div>
        {isLoading ? (
          <div className='flex flex-row items-center gap-2 h-[200px] justify-center'>
            <Loader2 className='size-10 animate-spin' />
          </div>
        ) : (
          <div className='flex flex-col h-[200px] bg-white no-wrap overflow-y-auto'>
            <input
              type="text"
              onChange={(e) => handleCompanyFilter(e)}
              placeholder='Search companies'
              className='w-full border-b border-gray-201 bg-white px-2 py-1 mr-2 mb-1 text-sm focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent'
            />
            { filteredCompanies.length > 0 ? filteredCompanies.map((company) => (
              <div
                key={company.id}
                onClick={() => handleCompanySelect(company)}
                className='flex flex-row items-center text-sm py-1 cursor-pointer hover:bg-gray-201'
              >
                <div className='flex-1 text-ellipsis overflow-hidden whitespace-nowrap pl-2'>
                  {company.company_name}
                </div>
                {selectedCompanies.includes(company) ? <Check className='size-4 mr-2 flex-shrink-0 text-green-501' /> : null}
              </div>
            )) : (
              <div className='text-sm text-gray-500 text-center h-[200px] flex items-center justify-center'>
                <Building2 className='size-4 mr-1' />
                No companies found
              </div>
            ) }
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyMultiSelector;
